import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation, useSearchParams, Link, useBlocker } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { brandConfig } from '../../config/brands';
import { PRODUCTS_QUERY_KEY, useProduct, useDeleteProduct, useUploadProductImage, useUpdateProduct, useDeleteImpact } from '../../features/products/hooks';
import { getProduct, getProductImageOriginalDownload, setProductImageFromMaterial, importProductImageUrl } from '../../features/products/api';
import { adoptImageCandidates, clearUrlSummaryResume, importImageCandidates, loadUrlSummaryResume, mergeAttributes, type SourcePages, type UrlSummaryFormSnapshot, type UrlSummaryImageCandidate, type UrlSummaryNewBrandDraft } from '../../features/products/urlSummary';
import { useUrlSummaryResumeScope } from '../../features/products/useUrlSummaryResumeScope';
import type { ProductUrlSummaryApply } from './shared/ProductUrlSummaryPanel';
import { ProductUrlSummaryModal, UrlSummaryLauncher } from './shared/ProductUrlSummaryModal';
import { saveImageToCloud, saveImageToTemporary } from '../../features/materials/api';
import { apiClient } from '../../services/apiClient';
import { useToast } from '../../hooks/useToast';
import { useMaterialsPanelStore } from '../../stores/materialsPanelStore';
import { Button } from '../../components/common/Button';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { useConfirmDialog } from '../../components/common/useConfirmDialog';
import { BackButton } from '../../components/common/BackButton';
import { ToggleSwitch } from '../../components/common/ToggleSwitch';
import { CreateBrandModal } from '../../components/generation/scenario/CreateBrandModal';
import { useProductImageSelection } from './shared/useProductImageSelection';
import { useProductMaterialFolders } from './shared/useProductMaterialFolders';
import { useProductImageDialogs } from './shared/useProductImageDialogs';
import { ProductImageSlotsSection, type DroppedMaterial } from './shared/ProductImageSlotsSection';
import { rasterErrorMessage, useEnsureRasterAsset } from '../../features/materials/ensureRasterAsset';
import { isNonRasterOriginal } from '../../components/materials/shared/materialUtils';
import { ProductDetailsSection } from './shared/ProductDetailsSection';
import { toReorderItem, type ProductImageSlot } from './shared/productImageSlots';
import { MANUAL_SUB_SLOT_MAX, buildManualSlots, manualActionTooltips, manualReorderDisabledReason, parseManualSlotKey, type ManualSlotRef } from './manualProductSlots';
import { buildManualReorderPayload, isSameOrder, type ReorderItem } from './shared/imageSlotReorder';
import { getErrorMessage } from '../../utils/getErrorMessage';
import { PRODUCT_IMAGE_ACCEPT, isUploadableProductImage } from '../../features/products/imageFile';
import { LocalImagePreview } from './shared/LocalImagePreview';
import { ProductInfoEditTable } from './shared/ProductInfoEditTable';
import { complianceCandidateRulesets } from './shared/complianceCandidates';
import {
  formatIndustryCategories,
  type IndustryCategory,
} from '../../features/products/industry';
import {
  useComplianceSettings,
  type ComplianceSettingsResponse,
} from '../../features/compliance/api';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import '../../features/products/industry.css';
import './ProductsPageHeader.css';
import './ProductDetailPage.css';

// Record<string, boolean> をキーソート済み JSON 文字列にして安定比較する
function stableJson(obj: Record<string, boolean>): string {
  const keys = Object.keys(obj).sort();
  return JSON.stringify(keys.map((k) => [k, obj[k]]));
}

export default function ManualProductDetailPage() {
  const resumeScope = useUrlSummaryResumeScope();
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  // 画像クリックで選択済み素材に追加/解除する共通フロー (組織スコープの素材一覧を使う)
  // 未保存画像は案件リンク付きで個人フォルダへ保存する (リンクが無いと生成側で案件情報を使えない)
  const { isImageSelected, handleAddToSelection } = useProductImageSelection({
    saveForSelection: (imageUrl) => saveImageToTemporary(imageUrl, undefined, productId ? { product_id: productId, product_type: 'manual' } : undefined, 'manual_product'),
  });
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  // インライン編集
  const [isNewProduct, setIsNewProduct] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<{
    title: string;
    description: string;
    sku: string;
    barcode_value: string;
    brand_id: string;
    brand_name: string;
    product_type: string;
    category: string;
    industry_categories: IndustryCategory[];
    compliance_defaults: Record<string, boolean>;
  }>({
    title: '',
    description: '',
    sku: '',
    barcode_value: '',
    brand_id: '',
    brand_name: '',
    product_type: '',
    category: '',
    industry_categories: [],
    compliance_defaults: {},
  });
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());
  // URL 読み取りで採用した補足属性 (キー単位) と取得元。保存時に最新の attributes へマージして送る (§5.3)。
  // 採用が無ければ attributes は送らない (PUT は attributes を丸ごと置き換えるため)
  const [attributesPatch, setAttributesPatch] = useState<Record<string, string>>({});
  const [sourcePages, setSourcePages] = useState<SourcePages | null>(null);
  const [brandDraft, setBrandDraft] = useState<UrlSummaryNewBrandDraft | null>(null);
  const editFormRef = useRef(editForm);
  editFormRef.current = editForm;
  // URL から読み取る (モーダル)。閲覧モードからも開け、「反映」で編集モードに入って採用した値を入れる
  const [summaryOpen, setSummaryOpen] = useState(false);
  const isEditingRef = useRef(isEditing);
  isEditingRef.current = isEditing;
  // 値は useProduct の後で入れる (クエリより前に getSummaryForm を定義しているため)
  const productDataRef = useRef<ReturnType<typeof useProduct>['data']>(undefined);
  const getSummaryForm = useCallback((): UrlSummaryFormSnapshot => {
    // 閲覧モードでは保存済みの値を「現在の値」にする (反映すると編集モードがこの値から始まるため)
    const p = productDataRef.current;
    if (!isEditingRef.current && p) {
      return {
        title: p.title || '',
        description: p.description || '',
        brand_name: p.brand_name || '',
        brand_id: p.brand_id || '',
        product_type: p.product_type || '',
        category: Array.isArray(p.category) ? p.category.join(', ') : '',
        industry_categories: p.industry_categories || [],
      };
    }
    const f = editFormRef.current;
    return {
      title: f.title,
      description: f.description,
      brand_name: f.brand_name,
      brand_id: f.brand_id,
      product_type: f.product_type,
      category: f.category,
      industry_categories: f.industry_categories,
    };
  }, []);
  // 読み取った商品画像の候補を空いている枠 (メイン → サブ) に入れる (§17)。案件行があるので 1 回の PUT で保存する (画像枠の操作と同じ経路)。
  // 項目の反映と一緒に呼ぶ (候補を選ぶ欄は出さない・利用者指示 2026-11-05)
  const applySummaryImages = async (candidates: UrlSummaryImageCandidate[]) => {
    if (!productId) return;
    // まず自社ストレージに取り込む (SVG は原本 + ラスタ版)。サイトの URL のままでは SVG を素材化できない
    const { imported, failed } = await importImageCandidates(candidates, importProductImageUrl);
    if (failed > 0) toast.error(`読み取った画像のうち${failed}件を取り込めませんでした`);
    const current = productDataRef.current;
    if (imported.length === 0 || !current) return;
    const base = {
      main_image_url: current.main_image_url || '',
      main_image_name: current.main_image_name ?? null,
      main_image_original_path: current.main_image_original_path ?? null,
      additional_images: [...(current.additional_images || [])],
    };
    const { images, added, mainChanged } = adoptImageCandidates(base, imported, MANUAL_SUB_SLOT_MAX);
    if (added === 0) return;
    try {
      await updateProduct.mutateAsync({
        id: productId,
        data: {
          // 別画像になるため名前は候補の alt (無ければ選定理由)、原本 (SVG) は取り込んだものに置き換える
          ...(mainChanged ? { main_image_url: images.main_image_url, main_image_name: images.main_image_name ?? null, main_image_original_path: images.main_image_original_path ?? null } : {}),
          additional_images: images.additional_images,
        },
      });
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      toast.success(`読み取った画像${added}件を画像枠に入れました`);
    } catch (err) {
      toast.error(`画像の追加に失敗しました: ${getErrorMessage(err)}`);
    }
  };

  const applySummary = ({ adoption, sourcePages: pages, brand, images = [] }: ProductUrlSummaryApply) => {
    // 閲覧モードからの反映は、保存済みの値で編集モードを始めてから採用した値を重ねる (どちらも setState なので順に反映される)
    if (!isEditingRef.current) startEditing();
    const { industry_categories, ...fields } = adoption.fields;
    setEditForm((prev) => ({
      ...prev,
      ...fields,
      ...(industry_categories ? { industry_categories } : {}),
      ...(brand ? { brand_id: brand.brand_id, brand_name: brand.brand_name } : {}),
    }));
    setAttributesPatch((prev) => ({ ...prev, ...adoption.attributes }));
    if (pages) setSourcePages(pages);
    toast.success('読み取った内容を反映しました。確認して保存してください');
    if (images.length > 0) void applySummaryImages(images);
  };

  // 画像アップロードモーダル
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadSlot, setUploadSlot] = useState<{ type: 'main' } | { type: 'sub'; index: number } | null>(null);
  const [uploadMode, setUploadMode] = useState<'local' | 'url'>('local');
  const [uploadUrl, setUploadUrl] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);


  // 削除確認モーダル
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const deleteImpactQuery = useDeleteImpact(productId, { enabled: deleteConfirmOpen });

  const productLabel = brandConfig.labels.productShort;
  const renderDeleteConfirmMessage = (): ReactNode => {
    if (deleteImpactQuery.isLoading) {
      return <span>影響範囲を確認中...</span>;
    }
    const impact = deleteImpactQuery.data;
    if (!impact || impact.total === 0) {
      return (
        <span>
          この{productLabel}に紐付く素材はありません。削除すると{productLabel}は復元 API で戻せます。
        </span>
      );
    }
    const othersCount = impact.others_personal + impact.others_shared;
    return (
      <>
        <div>
          この{productLabel}には素材 <strong>{impact.total} 件</strong> が紐付いています。
        </div>
        {othersCount > 0 && (
          <div style={{ color: '#b91c1c', marginTop: '6px' }}>
            そのうち <strong>{othersCount} 件</strong> は他のメンバーが追加したものです
            {' '}(内訳: 個人フォルダ {impact.others_personal} 件 / 共有フォルダ {impact.others_shared} 件)。
          </div>
        )}
        <div style={{ marginTop: '12px' }}>
          削除すると素材との紐付きは解除されますが、素材自体は削除されません。
        </div>
        <div style={{ fontSize: '0.9em', color: '#6b7280', marginTop: '4px' }}>
          {productLabel}は復元 API で戻せますが、素材との紐付きは復元されません。
        </div>
      </>
    );
  };

  // 画像枠の並べ替えモード: 編集中の並び [main, sub0..sub7] (null = 通常表示)
  const [reorderItems, setReorderItems] = useState<ReorderItem[] | null>(null);
  const [isSavingReorder, setIsSavingReorder] = useState(false);

  const { data: productData, isLoading, error } = useProduct(productId || '');
  productDataRef.current = productData;
  // 取得前・新規登録直後 (名前が空) は undefined → routes.tsx の handle が使われる
  useDocumentTitle(productData?.title || undefined);
  const deleteProduct = useDeleteProduct();
  const uploadImage = useUploadProductImage();
  const updateProduct = useUpdateProduct();
  // 素材パネルからドロップされた非ラスタ原本 (SVG) のラスタ版を取得する
  const ensureRasterAsset = useEnsureRasterAsset();
  // コンプライアンスルール一覧 + 組織マスタースイッチ。staleTime=5min でキャッシュされる。
  const { data: complianceSettings } = useComplianceSettings();

  // ブランド一覧
  const { data: brandsData } = useQuery({
    queryKey: ['brands'],
    queryFn: async () => {
      const response = await apiClient.get('/brands');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
  const brands: { id: string; brand_name: string }[] = brandsData?.brands ?? [];

  // ブランド新規作成モーダル (編集モードのブランド選択から開く)
  const [showCreateBrandModal, setShowCreateBrandModal] = useState(false);
  // 作成直後のブランド。一覧 (['brands'] クエリ) に載ったらブランド名を反映する
  const [createdBrandId, setCreatedBrandId] = useState<string | null>(null);

  useEffect(() => {
    if (!createdBrandId) return;
    const created = brands.find((b) => b.id === createdBrandId);
    if (!created) return;
    // URL 読み取りのモーダルを閲覧モードから開いて作成した場合は、ここで編集モードに入る
    // (後の「反映」で編集フォームを保存済みの値から作り直すと、作成したブランドが消えるため)
    if (!isEditingRef.current) startEditing();
    // 既存の選択操作と同じく、ブランド名も作成したブランドの名前で上書きする
    setEditForm((prev) => ({ ...prev, brand_id: created.id, brand_name: created.brand_name }));
    setCreatedBrandId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- startEditing は描画ごとに作り直される関数で、作成したブランドが一覧に載ったときだけ動けばよい
  }, [createdBrandId, brands]);

  // フォルダ別カード (素材閲覧 + 一括アップロード) は全パートナーで表示。
  // ページ離脱時 (戻る等) に、画像差し替えで開いた素材パネルを閉じる。
  // 開いたままだと別ページへ遷移してもパネルが残ってしまうため。
  useEffect(() => {
    return () => {
      useMaterialsPanelStore.getState().close();
    };
  }, []);

  // navigation stateから編集モード自動起動
  useEffect(() => {
    if (location.state?.editing && productData && !isEditing) {
      if (location.state?.isNew) setIsNewProduct(true);
      setIsEditing(true);
      setEditForm({
        title: location.state?.isNew ? '' : (productData.title || ''),
        description: productData.description || '',
        sku: productData.sku || '',
        barcode_value: productData.barcode_value || '',
        brand_id: productData.brand_id || '',
        brand_name: productData.brand_name || '',
        product_type: productData.product_type || '',
        category: Array.isArray(productData.category) ? productData.category.join(', ') : '',
        industry_categories: productData.industry_categories || [],
        compliance_defaults: productData.compliance_defaults ?? {},
      });
      // stateをクリアして再マウント時に再度発火しないようにする
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, productData]);

  // URLクエリパラメータから編集モード自動起動（新タブで開いた場合）
  useEffect(() => {
    if (searchParams.get('edit') === '1' && productData && !isEditing) {
      if (searchParams.get('isNew') === '1') setIsNewProduct(true);
      setIsEditing(true);
      setEditForm({
        title: productData.title || '',
        description: productData.description || '',
        sku: productData.sku || '',
        barcode_value: productData.barcode_value || '',
        brand_id: productData.brand_id || '',
        brand_name: productData.brand_name || '',
        product_type: productData.product_type || '',
        category: Array.isArray(productData.category) ? productData.category.join(', ') : '',
        industry_categories: productData.industry_categories || [],
        compliance_defaults: productData.compliance_defaults ?? {},
      });
      // クエリパラメータをクリアして再発火を防止
      navigate(location.pathname, { replace: true });
    }
  }, [searchParams, productData]);

  const isDirty = useCallback(() => {
    if (!isEditing || !productData) return false;
    if (isNewProduct) return true;
    if (Object.keys(attributesPatch).length > 0 || sourcePages) return true;
    return (
      editForm.title !== (productData.title || '') ||
      editForm.description !== (productData.description || '') ||
      editForm.sku !== (productData.sku || '') ||
      editForm.barcode_value !== (productData.barcode_value || '') ||
      editForm.brand_id !== (productData.brand_id || '') ||
      editForm.brand_name !== (productData.brand_name || '') ||
      editForm.product_type !== (productData.product_type || '') ||
      editForm.category !== (Array.isArray(productData.category) ? productData.category.join(', ') : '') ||
      JSON.stringify([...editForm.industry_categories].sort()) !==
        JSON.stringify([...(productData.industry_categories || [])].sort()) ||
      stableJson(editForm.compliance_defaults) !==
        stableJson(productData.compliance_defaults ?? {})
    );
  }, [isEditing, isNewProduct, editForm, productData, attributesPatch, sourcePages]);

  // ブラウザの戻る・リロード時の警告
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty()) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // SPA 内の遷移 (戻るボタン・ヘッダーのリンク等、パスが変わるもの) は useBlocker で止めて共通の ConfirmDialog を出す
  // (動画エディタと同じ方式)。新規登録の下書きも確認してから削除する。タブを閉じる / リロードは上の beforeunload
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    currentLocation.pathname !== nextLocation.pathname && isDirty(),
  );
  const leaveDiscarding = () => {
    if (isNewProduct && productId) deleteProduct.mutate(productId);
    blocker.proceed?.();
  };
  // 編集の「キャンセル」で変更を破棄する確認
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  const manualProductLink = productId ? { product_id: productId, product_type: 'manual' as const } : undefined;

  const saveToCloudMutation = useMutation({
    mutationFn: ({ url }: { url: string }) => saveImageToCloud(url, undefined, manualProductLink),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cloud-materials'] });
      queryClient.invalidateQueries({ queryKey: ['cloud-materials-all'] });
      queryClient.invalidateQueries({ queryKey: ['product-linked-materials', 'cloud', productId] });
      toast.success('画像を共有フォルダにアップロードしました');
    },
    onError: (error: Error) => toast.error(`アップロードに失敗しました: ${error.message}`),
  });

  const saveToTemporaryMutation = useMutation({
    mutationFn: ({ url }: { url: string }) => saveImageToTemporary(url, undefined, manualProductLink, 'manual_product'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['temporary-materials'] });
      queryClient.invalidateQueries({ queryKey: ['temporary-materials-all'] });
      queryClient.invalidateQueries({ queryKey: ['product-linked-materials', 'temporary', productId] });
      toast.success('画像を個人フォルダにアップロードしました');
    },
    onError: (error: Error) => toast.error(`アップロードに失敗しました: ${error.message}`),
  });

  // ※ 選択フロー (isImageSelected / handleAddToSelection) は組織スコープの素材一覧を
  //   使い続ける (useProductImageSelection)。既存共有素材をそのまま選択に使えるように
  //   するため。商品スコープにすると個人フォルダ保存へ誤フォールバックする。

  // 全画像URLを収集
  const allImageUrls: string[] = [];
  if (productData?.main_image_url?.trim()) allImageUrls.push(productData.main_image_url);
  if (productData?.additional_images) {
    for (const img of productData.additional_images) {
      if (img.url?.trim()) allImageUrls.push(img.url);
    }
  }

  // ヘッダーのフォルダ別カード (件数・アップロード済み判定・一括アップロード) は共通フック
  const folders = useProductMaterialFolders({
    productId,
    productType: 'manual',
    imageUrls: allImageUrls,
    saveToCloud: (url) => saveImageToCloud(url, undefined, manualProductLink),
    saveToTemporary: (url) => saveImageToTemporary(url, undefined, manualProductLink, 'manual_product'),
    card: {
      helpText: (folder) => folder === 'shared'
        ? 'このプロジェクトに登録した画像（プロジェクト画像）を共有フォルダに「素材」としてアップロードすると、組織のメンバー全員が制作スタジオなどで利用できるようになります。'
        : 'このプロジェクトに登録した画像（プロジェクト画像）を個人フォルダに「素材」としてアップロードすると、あなただけが制作スタジオなどで利用できるようになります。',
      countLabel: `この${brandConfig.labels.productShort}の${brandConfig.labels.material}数`,
      imageNoun: `${brandConfig.labels.productShort}画像`,
    },
  });
  const { isImageSaved, isImageSavedInTemporary, materialLinksEnabled, renderFolderCard } = folders;

  const handleDelete = () => {
    if (!productId) return;
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!productId) return;
    deleteProduct.mutate(productId, {
      onSuccess: () => {
        setDeleteConfirmOpen(false);
        toast.success(`${brandConfig.labels.productShort}を削除しました`);
        navigate('/products/manual');
      },
      onError: (err: Error) => {
        toast.error(`削除に失敗しました: ${err.message}`);
      },
    });
  };

  const startEditing = () => {
    if (!productData) return;
    setEditForm({
      title: productData.title || '',
      description: productData.description || '',
      sku: productData.sku || '',
      barcode_value: productData.barcode_value || '',
      brand_id: productData.brand_id || '',
      brand_name: productData.brand_name || '',
      product_type: productData.product_type || '',
      category: Array.isArray(productData.category) ? productData.category.join(', ') : '',
      industry_categories: productData.industry_categories || [],
      compliance_defaults: productData.compliance_defaults ?? {},
    });
    setAttributesPatch({});
    setSourcePages(null);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    if (isDirty()) {
      setCancelConfirmOpen(true);
      return;
    }
    discardEditing();
  };

  const discardEditing = () => {
    setCancelConfirmOpen(false);
    if (isNewProduct && productId) {
      setIsNewProduct(false);
      setIsEditing(false);
      deleteProduct.mutate(productId, {
        onSuccess: () => {
          navigate('/products/manual');
        },
      });
      return;
    }
    setIsEditing(false);
    setValidationErrors(new Set());
    setAttributesPatch({});
    setSourcePages(null);
  };

  const saveEditing = async () => {
    if (!productId) return;
    const errors = new Set<string>();
    if (!editForm.title.trim()) errors.add('title');
    if (errors.size > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors(new Set());
    setIsSaving(true);
    try {
      // URL 読み取りで採用した補足属性があるときだけ attributes を送る。保存直前に案件を再取得し、
      // その最新の attributes を土台に採用したキーだけを上書きする (別タブの編集を上書きしにくくする・§5.3)
      let attributes: Record<string, unknown> | undefined;
      if (Object.keys(attributesPatch).length > 0 || sourcePages) {
        const latest = await getProduct(productId);
        attributes = mergeAttributes(latest.attributes, attributesPatch);
        if (sourcePages) attributes.source_pages = sourcePages;
      }
      await updateProduct.mutateAsync({
        id: productId,
        data: {
          title: editForm.title,
          description: editForm.description,
          sku: editForm.sku,
          barcode_value: editForm.barcode_value,

          brand_id: editForm.brand_id || null,
          brand_name: editForm.brand_name,
          product_type: editForm.product_type,
          category: editForm.category.split(/[,、]/).map(s => s.trim()).filter(Boolean),
          industry_categories: editForm.industry_categories.length > 0 ? editForm.industry_categories : null,
          compliance_defaults: editForm.compliance_defaults,
          ...(attributes ? { attributes } : {}),
        },
      });
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      if (resumeScope) clearUrlSummaryResume(resumeScope, productId); // 引き継いでいた読み取りは保存で役目を終える
      toast.success(`${productLabel}情報を更新しました`);
      setIsNewProduct(false);
      setIsEditing(false);
      setAttributesPatch({});
      setSourcePages(null);
    } catch (err: any) {
      toast.error(`更新に失敗しました: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteImage = async (slot: { type: 'main' } | { type: 'sub'; index: number }) => {
    if (!productId || !productData) return;
    // ブラウザの confirm は出ない環境があるので独自モーダル (利用者指示 2026-10-30)
    if (!(await confirmAction({ title: 'この画像を削除しますか？', confirmLabel: '削除する' }))) return;
    try {
      if (slot.type === 'main') {
        // 名前は画像内容の説明のため、画像削除と同時にクリアする
        await updateProduct.mutateAsync({ id: productId, data: { main_image_url: '', main_image_name: null } });
      } else {
        const newAdditional = [...(productData.additional_images || [])];
        newAdditional.splice(slot.index, 1);
        await updateProduct.mutateAsync({ id: productId, data: { additional_images: newAdditional } });
      }
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      toast.success('画像を削除しました');
    } catch (err: any) {
      toast.error(`画像の削除に失敗しました: ${err.message}`);
    }
  };

  const openUploadModal = (slot: { type: 'main' } | { type: 'sub'; index: number }) => {
    setUploadSlot(slot);
    setUploadMode('local');
    setUploadUrl('');
    setUploadFile(null);
    setUploadPreview(null);
    setUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    setUploadModalOpen(false);
    setUploadSlot(null);
    setUploadFile(null);
    setUploadPreview(null);
    setUploadUrl('');
  };

  const processFile = (file: File) => {
    if (!isUploadableProductImage(file)) {
      toast.error('画像ファイルを選択してください');
      return;
    }
    setUploadFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setUploadPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  // 枠に画像 URL を保存する (メイン画像は名前もクリア、追加画像は該当 index に差し込む)。
  // originalPath = SVG アップロードで返る原本のパス。url と同じ PUT で送る (省略して url を変えると旧原本は解除される)
  const saveSlotUrl = async (slot: ManualSlotRef, url: string, originalPath?: string | null) => {
    if (!productId) return;
    if (slot.type === 'main') {
      // 別画像になるため名前 (画像内容の説明) はクリアする
      await updateProduct.mutateAsync({
        id: productId,
        data: { main_image_url: url, main_image_name: null, main_image_original_path: originalPath || null },
      });
    } else {
      const newAdditional = [...(productData?.additional_images || [])];
      while (newAdditional.length <= slot.index) {
        newAdditional.push({ url: '' });
      }
      newAdditional[slot.index] = originalPath ? { url, original_path: originalPath } : { url };
      await updateProduct.mutateAsync({ id: productId, data: { additional_images: newAdditional } });
    }
    queryClient.invalidateQueries({ queryKey: ['product', productId] });
  };

  const toSlotParam = (slot: ManualSlotRef): 'main' | number => (slot.type === 'main' ? 'main' : slot.index);

  // SVG 素材を枠に置く: サーバーが原本とラスタ版を案件側へ複製して url + original_path を保存する (計画 §3.5 後半)
  const setSlotFromSvgMaterial = async (slot: ManualSlotRef, material: { id: string; folderType: 'temporary' | 'cloud' }) => {
    if (!productId) return;
    await setProductImageFromMaterial(productId, { slot: toSlotParam(slot), folder_type: material.folderType, material_id: material.id });
    // useUpdateProduct を通らないので、同フックと同じキー (PRODUCTS_QUERY_KEY = useProduct の親キー) を無効化して再取得させる
    await queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY });
  };

  // SVG バッジ: 原本 (SVG) を署名URL (attachment) でダウンロード
  const handleDownloadOriginal = async (slot: ProductImageSlot) => {
    if (!productId) return;
    try {
      const { url, file_name } = await getProductImageOriginalDownload(productId, toSlotParam(toSlotRef(slot)));
      const a = document.createElement('a');
      a.href = url;
      a.download = file_name;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast.error(`原本のダウンロードに失敗しました: ${getErrorMessage(err, '取得できませんでした')}`);
    }
  };

  // 空き枠への直接ドロップ (ファイル)
  const handleDropFile = async (slot: ManualSlotRef, file: File) => {
    if (!productId) return;
    if (!isUploadableProductImage(file)) {
      toast.error('画像ファイルを選択してください');
      return;
    }
    try {
      const result = await uploadImage.mutateAsync(file);
      await saveSlotUrl(slot, result.url, result.original_path ?? null);
      toast.success('画像をアップロードしました');
    } catch (err: any) {
      toast.error(`アップロードに失敗しました: ${getErrorMessage(err)}`);
    }
  };

  // 空き枠への直接ドロップ (素材パネルの素材カード)。
  // 素材パネルで選んだときと同じく、ラスタ画像の URL をそのまま枠に保存する (再アップロードはしない)。
  // 非ラスタ原本 (SVG) はラスタ版を取得してから保存。動画・音声は画像として使えないので断る
  const handleDropMaterial = async (slot: ManualSlotRef, material: DroppedMaterial) => {
    if (!productId) return;
    const contentType = material.contentType || '';
    if (contentType.startsWith('video/') || contentType.startsWith('audio/')) {
      toast.error('画像素材のみ設定できます');
      return;
    }
    if (isNonRasterOriginal(contentType)) {
      // SVG 素材は URL を書くだけだと原本が切れるので、サーバー側で原本とラスタ版を複製する
      try {
        await setSlotFromSvgMaterial(slot, { id: material.id, folderType: material.type });
        toast.success('画像を設定しました');
      } catch (err) {
        toast.error(`画像の設定に失敗しました: ${getErrorMessage(err, '保存できませんでした')}`);
      }
      return;
    }
    let url = '';
    try {
      url = ((await ensureRasterAsset(material)).sourceUrl || '').trim();
    } catch (err) {
      toast.error(rasterErrorMessage(err));
      return;
    }
    if (!url) {
      toast.error('この素材は画像として使用できません');
      return;
    }
    try {
      await saveSlotUrl(slot, url);
      toast.success('画像を設定しました');
    } catch (err) {
      toast.error(`画像の設定に失敗しました: ${getErrorMessage(err, '保存できませんでした')}`);
    }
  };

  const handleUploadConfirm = async () => {
    if (!productId || !uploadSlot) return;
    setIsUploading(true);
    try {
      let imageUrl: string;
      let originalPath: string | null = null;
      if (uploadMode === 'local') {
        if (!uploadFile) { toast.error('ファイルを選択してください'); setIsUploading(false); return; }
        const result = await uploadImage.mutateAsync(uploadFile);
        imageUrl = result.url;
        originalPath = result.original_path ?? null; // SVG のときだけ付く
      } else {
        if (!uploadUrl.trim()) { toast.error('URLを入力してください'); setIsUploading(false); return; }
        // URL のまま参照せず自社ストレージに取り込む (SVG は原本 + ラスタ版・§17)
        const result = await importProductImageUrl(uploadUrl.trim());
        imageUrl = result.url;
        originalPath = result.original_path ?? null;
      }

      await saveSlotUrl(uploadSlot, imageUrl, originalPath);
      toast.success('画像を設定しました');
      closeUploadModal();
    } catch (err: any) {
      toast.error(`画像の設定に失敗しました: ${getErrorMessage(err)}`);
    } finally {
      setIsUploading(false);
    }
  };

  // ---- 共通の画像枠セクションに渡す枠モデル (アダプタ) ----
  // DB の main_image_url / main_image_name / additional_images から組み立てる ({url: ''} の穴は空き枠)
  const productSlots: ProductImageSlot[] = buildManualSlots(productData);
  const toSlotRef = (slot: ProductImageSlot): ManualSlotRef => parseManualSlotKey(slot.key) ?? { type: 'main' };

  // 画像差し替え（素材選択パネルを開く。選んだら差替確認ダイアログへ）
  const handleReplaceImage = (slot: ProductImageSlot) => {
    useMaterialsPanelStore.getState().open('select', {
      targetBrandId: productData?.brand_id || undefined,
      targetSlot: slot.key,
      onSelect: (imageUrl, material) => {
        closeUploadModal();
        dialogs.openReplaceConfirm(slot, imageUrl, material);
      }
    });
  };

  // 差替確認・名前編集ダイアログ (共通フック)。
  // 差替は保存の完了を待ってから閉じる (成功・失敗どちらでも閉じる。結果はトースト)。
  // 名前は成功時だけ閉じ、失敗時は再 throw してダイアログに入力を残す
  // 削除などの確認 (window.confirm の代わり)
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirmDialog();
  const dialogs = useProductImageDialogs({
    replaceTitle: (slot) => `マニュアル登録の${slot.label}を差し替えますか？`,
    onConfirmReplace: async (slot, newUrl, source) => {
      if (!productId) return;
      const ref = toSlotRef(slot);
      try {
        if (source && isNonRasterOriginal(source.contentType)) {
          // SVG 素材: 原本を引き継ぐためサーバー側で複製 (URL だけ書くと原本が切れる)
          await setSlotFromSvgMaterial(ref, source);
        } else {
          // ラスタ画像: URL をそのまま保存 (原本は付かない。別画像になるので旧原本は解除される)
          await saveSlotUrl(ref, newUrl);
        }
        toast.success('画像を差し替えました');
      } catch (err) {
        toast.error(`画像の差し替えに失敗しました: ${getErrorMessage(err, '保存できませんでした')}`);
      }
    },
    // 画像の名前を保存 (空文字 = クリア)。
    // 追加画像は additional_images の該当要素に name をインライン保存、メイン画像は main_image_name カラムに保存する
    onSaveName: async (slot, name) => {
      if (!productId || !productData) return;
      const ref = toSlotRef(slot);
      try {
        if (ref.type === 'main') {
          await updateProduct.mutateAsync({ id: productId, data: { main_image_name: name || null } });
        } else {
          const newAdditional = (productData.additional_images || []).map((img, i) =>
            i === ref.index ? { ...img, name: name || undefined } : img
          );
          await updateProduct.mutateAsync({ id: productId, data: { additional_images: newAdditional } });
        }
        queryClient.invalidateQueries({ queryKey: ['product', productId] });
        toast.success(name ? '画像の名前を更新しました' : '画像の名前をクリアしました');
      } catch (err) {
        toast.error(`名前の更新に失敗しました: ${getErrorMessage(err, '保存できませんでした')}`);
        throw err;
      }
    },
  });

  // ---- 画像枠の並べ替えモード ----
  // 「並べ替え」でメイン画像も含めた全部のカードをつかんで動かせるようにし、「完了」で
  // main_image_url / main_image_name / additional_images を 1 回の PUT で保存する。
  // 配列契約: items は main, sub-0.. の枠の数だけの要素 (末尾は追加用の空き枠)。名前 (name) と width / height (meta) は要素と一緒に動く
  const currentReorderItems = (): ReorderItem[] => productSlots.map(toReorderItem);
  const reorderDisabledReason: string | null = manualReorderDisabledReason({
    slots: productSlots,
    isEditing,
    isSaving: updateProduct.isPending,
  });
  const canStartReorder = reorderDisabledReason === null;
  const reorderDirty = !!reorderItems && !isSameOrder(currentReorderItems(), reorderItems);

  const startReorder = () => {
    if (!canStartReorder) return;
    setReorderItems(currentReorderItems());
  };

  const cancelReorder = () => {
    if (isSavingReorder) return;
    setReorderItems(null);
  };

  const completeReorder = async () => {
    if (!reorderItems || !productId || !productData || isSavingReorder) return;
    if (!reorderDirty) {
      setReorderItems(null);
      return;
    }
    setIsSavingReorder(true);
    try {
      const data = buildManualReorderPayload(reorderItems, (productData.additional_images || []).slice(reorderItems.length - 1));
      await updateProduct.mutateAsync({ id: productId, data });
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      toast.success('画像の並び順を保存しました');
      setReorderItems(null);
    } catch (err) {
      toast.error(`並べ替えに失敗しました: ${getErrorMessage(err, '保存できませんでした')}`);
    } finally {
      setIsSavingReorder(false);
    }
  };

  return (
    <div className="product-detail-page">
      <div className="header-actions">
        <BackButton />
      </div>

      <header className="page-header page-header--child">
        <div className="page-header-left">
          <h1 className="page-title">
            {!isLoading && productData ? productData.title || '商品詳細' : '商品詳細'}
          </h1>
          {!isEditing && !isLoading && productData && (
            <p className="page-description">
              {brandConfig.features.skuFields && productData.sku && `SKU: ${productData.sku}`}
              {(productData.brand_name || productData.brand_master_name) && (
                <>
                  {brandConfig.features.skuFields && productData.sku && ' | '}
                  {'ブランド: '}
                  {productData.brand_id ? (
                    <Link to={`/brands/${productData.brand_id}`} className="brand-link">
                      {brands.find(b => b.id === productData.brand_id)?.brand_name || productData.brand_master_name || productData.brand_name}
                    </Link>
                  ) : (
                    productData.brand_name
                  )}
                </>
              )}
            </p>
          )}
        </div>
        {materialLinksEnabled && (
          <div className="page-header-actions material-folder-actions">
            {renderFolderCard('shared')}
            {renderFolderCard('personal')}
          </div>
        )}
      </header>

      {isLoading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>読み込み中...</p>
        </div>
      ) : error ? (
        <div className="error-message">
          {productLabel}情報の取得に失敗しました
        </div>
      ) : !productData ? (
        <div className="error-message">
          商品データが見つかりません
        </div>
      ) : (
        <section className="product-result">
          {/* 画像セクション（上部）: 共通コンポーネント。編集モードは削除 (専用ゴミ箱)・空き枠クリック・ドロップだけ可 */}
          <ProductImageSlotsSection
            slots={productSlots}
            uniformLayout
            capabilities={{
              select: !isEditing,
              save: !isEditing,
              replace: !isEditing,
              delete: true,
              editName: !isEditing,
              fileDrop: true,
              emptyClick: true,
              reorder: true,
            }}
            deleteButton={isEditing ? 'trash' : 'actions'}
            actionTooltips={manualActionTooltips}
            isImageSelected={isImageSelected}
            onSelectImage={(slot) => slot.url && handleAddToSelection(slot.url)}
            onReplace={handleReplaceImage}
            onDelete={(slot) => void handleDeleteImage(toSlotRef(slot))}
            onEditName={dialogs.openNameEdit}
            onSaveName={dialogs.saveNameInline}
            onEmptyClick={(slot) => {
              openUploadModal(toSlotRef(slot));
              handleReplaceImage(slot);
            }}
            onDropFile={(slot, file) => handleDropFile(toSlotRef(slot), file)}
            onDropMaterial={(slot, material) => handleDropMaterial(toSlotRef(slot), material)}
            onDownloadOriginal={handleDownloadOriginal}
            saveButtons={{
              isSavedCloud: isImageSaved,
              isSavedTemp: isImageSavedInTemporary,
              pendingCloud: saveToCloudMutation.isPending,
              pendingTemp: saveToTemporaryMutation.isPending,
              onSaveCloud: (url) => saveToCloudMutation.mutate({ url }),
              onSaveTemp: (url) => saveToTemporaryMutation.mutate({ url }),
              verb: 'アップロード',
            }}
            noImageFallback
            reorder={{
              disabledReason: reorderDisabledReason,
              items: reorderItems,
              labelAt: (index) => (index === 0 ? 'メイン画像' : `画像 ${index}`),
              isDirty: reorderDirty,
              isSubmitting: isSavingReorder,
              submitLabel: '保存中...',
              hint: '画像カードのドラッグで並び替え',
              onStart: startReorder,
              onCancel: cancelReorder,
              onChange: setReorderItems,
              onComplete: completeReorder,
            }}
          />

          {/* 商品情報セクション (共通の枠組み)。下部ボタン行はボタン規約 (取り消し系=左 / 主アクション=右) に
              合わせ、編集モードはキャンセル/保存、表示モードは削除/編集 */}
          <ProductDetailsSection
            title={`${productLabel}情報`}
            headerActions={(
              <UrlSummaryLauncher
                onClick={() => setSummaryOpen(true)}
                pending={!!(resumeScope && loadUrlSummaryResume(resumeScope, productId))}
                disabled={isSaving}
              />
            )}
            footer={isEditing ? (
              <>
                <Button variant="danger" size="sm" onClick={cancelEditing} disabled={isSaving}>
                  キャンセル
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  className="btn-modal-sub"
                  onClick={saveEditing}
                  disabled={isSaving}
                >
                  {isSaving ? '保存中...' : '保存'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="danger"
                  size="md"
                  onClick={handleDelete}
                  disabled={deleteProduct.isPending}
                >
                  <img src="/bin_1_white.svg" alt="" className="delete-button-icon" />
                  {deleteProduct.isPending ? '削除中...' : `${productLabel}を削除`}
                </Button>
                <button
                  type="button"
                  className="btn-modal btn-modal-sm product-details-edit-btn"
                  onClick={startEditing}
                  aria-label={`${brandConfig.labels.productShort}情報を編集`}
                >
                  <img src="/pencil_1.svg" alt="" className="product-details-edit-icon" />
                  <span>編集</span>
                </button>
              </>
            )}
          >
            <table className="details-table">
              <tbody>
                {isEditing
                  ? (
                    <ProductInfoEditTable
                      editForm={editForm}
                      setEditForm={setEditForm}
                      validationErrors={validationErrors}
                      brands={brands}
                      complianceSettings={complianceSettings}
                      onCreateBrand={() => { setBrandDraft(null); setShowCreateBrandModal(true); }}
                    />
                  )
                  : renderDetailRows(productData, descriptionExpanded, setDescriptionExpanded, brands, complianceSettings)}
              </tbody>
            </table>
          </ProductDetailsSection>
        </section>
      )}
      {/* URL から読み取る (閲覧・編集どちらからでも開く。提案は項目ごとに採用してフォームへ反映し、保存は「保存」) */}
      <ProductUrlSummaryModal
        open={summaryOpen}
        onClose={() => setSummaryOpen(false)}
        productId={productId}
        getForm={getSummaryForm}
        brands={brands}
        onApply={applySummary}
        onCreateBrand={(draft) => { setBrandDraft(draft); setShowCreateBrandModal(true); }}
        disabled={isSaving}
      />
      {/* 画像差替確認・名前編集モーダル (共通フック) */}
      {dialogs.dialogs}
      {confirmDialog}

      {/* 画像アップロードモーダル */}
      {uploadModalOpen && createPortal(
        <div className="image-upload-modal-backdrop" onClick={closeUploadModal}>
          <div className="image-upload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="image-upload-modal-header">
              <h3>画像を設定</h3>
              <button className="modal-close-button" onClick={closeUploadModal}>×</button>
            </div>
            <div className="image-upload-modal-body">
              <div className="upload-mode-radio">
                <label className="radio-label">
                  <input type="radio" name="uploadMode" value="local" checked={uploadMode === 'local'} onChange={() => setUploadMode('local')} />
                  ローカルからアップロード
                </label>
                <label className="radio-label">
                  <input type="radio" name="uploadMode" value="url" checked={uploadMode === 'url'} onChange={() => setUploadMode('url')} />
                  URLを指定
                </label>
              </div>
              <p className="upload-panel-hint">この画面を閉じて、素材パネルから画像素材を選択することもできます</p>

              {uploadMode === 'local' ? (
                <div className="upload-local-section">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={PRODUCT_IMAGE_ACCEPT}
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />
                  {uploadPreview ? (
                    <div className="upload-preview" onClick={() => fileInputRef.current?.click()}
                      onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}>
                      <LocalImagePreview key={uploadPreview} src={uploadPreview} fileName={uploadFile?.name} />
                    </div>
                  ) : (
                    <div className={`upload-drop-area${isDragging ? ' dragging' : ''}`}
                      onClick={() => fileInputRef.current?.click()}
                      onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}>
                      <span className="upload-icon">+</span>
                      <span>クリックまたはドラッグ&ドロップで画像を選択</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="upload-url-section">
                  <label>画像URL</label>
                  <input
                    type="text"
                    value={uploadUrl}
                    onChange={(e) => setUploadUrl(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                  />
                  {uploadUrl.trim() && (
                    <div className="upload-preview">
                      <img src={uploadUrl} alt="プレビュー" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="image-upload-modal-footer">
              <Button variant="danger" size="md" onClick={closeUploadModal}>キャンセル</Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleUploadConfirm}
                disabled={isUploading || (uploadMode === 'local' ? !uploadFile : !uploadUrl.trim())}
              >
                {isUploading ? '設定中...' : '設定'}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        title={`この${productLabel}を削除しますか?`}
        variant="danger"
        confirmLabel="削除する"
        isLoading={deleteProduct.isPending}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        message={renderDeleteConfirmMessage()}
      />

      {/* 未保存の変更があるときの確認 (別ページへの移動・編集のキャンセル) */}
      {blocker.state === 'blocked' && (
        <ConfirmDialog
          isOpen
          variant="warning"
          title={isNewProduct ? `${productLabel}の登録が完了していません` : '未保存の変更があります'}
          message={isNewProduct
            ? `このまま移動すると、作成中の${productLabel}は削除されます。移動しますか？`
            : '保存していない変更は失われます。このまま移動しますか？'}
          confirmLabel="破棄して移動"
          cancelLabel="編集を続ける"
          onConfirm={leaveDiscarding}
          onCancel={() => blocker.reset?.()}
        />
      )}
      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        variant="warning"
        title={isNewProduct ? `${productLabel}の登録をやめますか？` : '未保存の変更があります'}
        message={isNewProduct
          ? `作成中の${productLabel}は削除されます。`
          : '保存していない変更は失われます。編集をやめますか？'}
        confirmLabel={isNewProduct ? '登録をやめる' : '破棄する'}
        cancelLabel="編集を続ける"
        onConfirm={discardEditing}
        onCancel={() => setCancelConfirmOpen(false)}
      />

      {/* ブランド新規作成 (編集中のフォーム内容はそのまま保持される) */}
      <CreateBrandModal
        isOpen={showCreateBrandModal}
        onClose={() => setShowCreateBrandModal(false)}
        onBrandCreated={(brandId) => setCreatedBrandId(brandId)}
        initialValues={brandDraft ? { brand_name: brandDraft.brand_name, brand_code: brandDraft.brand_code ?? undefined, tagline: brandDraft.tagline, description: brandDraft.description, site_url_candidates: brandDraft.official_site_candidates ?? [] } : null}
        startAt={brandDraft ? 'confirm' : 'url'}
      />
    </div>
  );
}

function renderDetailRows(
  productData: any,
  descriptionExpanded: boolean,
  setDescriptionExpanded: (v: boolean) => void,
  brands: { id: string; brand_name: string }[],
  complianceSettings: ComplianceSettingsResponse | undefined,
) {
  // 商品の業界カテゴリと交差するルール + 横断ルールを候補とする。
  const complianceCandidates = complianceCandidateRulesets(
    complianceSettings,
    productData.industry_categories ?? [],
  );
  const complianceDefaults: Record<string, boolean> = productData.compliance_defaults ?? {};
  return (
    <>
      {/* 商品名 */}
      {productData.title && (
        <tr>
          <th>{brandConfig.labels.productName}</th>
          <td colSpan={3}>{productData.title}</td>
        </tr>
      )}

      {/* 説明 */}
      {productData.description && (
        <tr>
          <th>{brandConfig.labels.productDescription}</th>
          <td colSpan={3} className="description-cell">
            <div className={`description-content ${descriptionExpanded ? 'expanded' : 'collapsed'}`}>
              <div className="description-text">
                {descriptionExpanded
                  ? productData.description
                  : productData.description.length > 150
                  ? `${productData.description.substring(0, 150)}...`
                  : productData.description}
              </div>
              {productData.description.length > 150 && (
                <div
                  className="expand-toggle"
                  onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                >
                  <span className={`toggle-icon ${descriptionExpanded ? 'expanded' : ''}`}>
                    ›
                  </span>
                  <span className="toggle-text">
                    {descriptionExpanded ? '折りたたむ' : 'もっと見る'}
                  </span>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}

      {/* SKU + JANコード */}
      {brandConfig.features.skuFields && (
        <tr>
          <th>SKU</th>
          <td>{productData.sku || '-'}</td>
          <th>JANコード</th>
          <td>
            {productData.barcode_value || '-'}
          </td>
        </tr>
      )}

      {/* ブランド（自由入力） + ブランド */}
      <tr>
        <th>ブランド（自由入力）</th>
        <td>{productData.brand_name || '-'}</td>
        <th>ブランド</th>
        <td>
          {productData.brand_id ? (
            <Link to={`/brands/${productData.brand_id}`} className="brand-link">
              {brands.find(b => b.id === productData.brand_id)?.brand_name || productData.brand_name || '-'}
            </Link>
          ) : (
            '-'
          )}
        </td>
      </tr>

      {/* カテゴリ + 商品種別 */}
      <tr>
        <th>カテゴリ</th>
        <td>
          {productData.category && productData.category.length > 0
            ? productData.category.join('、')
            : '-'}
        </td>
        <th>{brandConfig.labels.productShort}種別</th>
        <td>{productData.product_type || '-'}</td>
      </tr>

      {/* 特定カテゴリ（薬機法対応） + 景表法のケア (該当ルールがあるときのみ右列に表示) */}
      {/* 2 値化: 未設定キーは「有効」扱い (該当ルールは自動判定で有効になるため) */}
      {/* 表示ビューはトグル disabled、編集ビューは操作可能 */}
      <tr>
        <th>特定カテゴリ</th>
        <td colSpan={complianceCandidates.length > 0 ? 1 : 3}>
          {formatIndustryCategories(productData.industry_categories)}
        </td>
        {complianceCandidates.length > 0 && (
          <>
            <th>景表法のケア</th>
            <td>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {complianceCandidates.map((rule) => {
                  const v = complianceDefaults[rule.code];
                  const isEnabled = v !== false;
                  return (
                    <ToggleSwitch
                      key={rule.code}
                      checked={isEnabled}
                      onChange={() => undefined}
                      disabled
                      label={isEnabled ? '有効' : '無効'}
                    />
                  );
                })}
              </div>
            </td>
          </>
        )}
      </tr>

      {/* 登録元 */}
      <tr>
        <th>登録元</th>
        <td colSpan={3}>{{ manual: '手入力', csv: 'CSV', excel: 'Excel' }[productData.source as string] ?? productData.source}</td>
      </tr>

      {/* 登録日 + 更新日 */}
      <tr>
        <th>登録日</th>
        <td>
          {productData.created_at
            ? new Date(productData.created_at).toLocaleString('ja-JP')
            : '-'}
        </td>
        <th>更新日</th>
        <td>
          {productData.updated_at
            ? new Date(productData.updated_at).toLocaleString('ja-JP')
            : '-'}
        </td>
      </tr>
    </>
  );
}

